import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navigation } from '../../components/Navigation';
import { Footer } from '../../components/Footer';
import { SideCart } from '../../components/SideCart';
import { ChatBot } from '../../components/ChatBot';
import { ScrollAnimatedSection } from '../../components/ScrollAnimatedSection';
import { useCart } from '../../context/CartContext';
import { getPostBySlug, getRelatedPosts } from '../../data/blogData';
import { Clock, Calendar, ArrowLeft, ArrowRight, User, Tag } from 'lucide-react';
import { Button } from '../../components/Button';

export function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();
  
  const post = getPostBySlug(slug);
  const relatedPosts = getRelatedPosts(slug, 2);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!post) return;

    const originalTitle = document.title;
    const pageUrl = `https://berleto.com/blog/${post.slug}`;
    const metadata = [
      ['meta[name="description"]', 'content', post.excerpt],
      ['link[rel="canonical"]', 'href', pageUrl],
      ['meta[property="og:title"]', 'content', post.title],
      ['meta[property="og:description"]', 'content', post.excerpt],
      ['meta[property="og:url"]', 'content', pageUrl],
      ['meta[property="og:image"]', 'content', `https://berleto.com${post.image}`],
      ['meta[property="twitter:title"]', 'content', post.title],
      ['meta[property="twitter:description"]', 'content', post.excerpt],
      ['meta[property="twitter:url"]', 'content', pageUrl],
      ['meta[property="twitter:image"]', 'content', `https://berleto.com${post.image}`]
    ];
    const originals = metadata.map(([selector, attribute, value]) => {
      const element = document.querySelector(selector);
      const original = element?.getAttribute(attribute);
      element?.setAttribute(attribute, value);
      return { element, attribute, original };
    });
    document.title = `${post.title} | Berleto Journal`;

    return () => {
      document.title = originalTitle;
      originals.forEach(({ element, attribute, original }) => {
        if (original === null) element?.removeAttribute(attribute);
        else if (original !== undefined) element?.setAttribute(attribute, original);
      });
    };
  }, [slug, post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-light text-berleto-warmgray mb-4">Article not found</h1>
          <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
        </div>
      </div>
    );
  }

  const renderSection = (section, index) => {
    switch (section.type) {
      case 'intro':
        return (
          <ScrollAnimatedSection key={index} delay={0.1}>
            <p className="text-xl md:text-2xl text-berleto-sage leading-relaxed mb-12 font-light">
              {section.text}
            </p>
          </ScrollAnimatedSection>
        );
      
      case 'heading':
        return (
          <ScrollAnimatedSection key={index} delay={0.1}>
            <h2 className="text-3xl md:text-4xl font-light text-berleto-warmgray mb-6 mt-12">
              {section.text}
            </h2>
          </ScrollAnimatedSection>
        );
      
      case 'text':
        return (
          <ScrollAnimatedSection key={index} delay={0.1}>
            <p className="text-lg text-berleto-sage leading-relaxed mb-6">
              {section.text}
            </p>
          </ScrollAnimatedSection>
        );
      
      case 'quote':
        return (
          <ScrollAnimatedSection key={index} delay={0.1}>
            <motion.div 
              className="my-12 relative"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-berleto-rust to-berleto-sage rounded-full"></div>
              <div className="pl-8 pr-6 py-6 bg-berleto-offwhite rounded-r-2xl">
                <p className="text-xl md:text-2xl text-berleto-warmgray font-light italic mb-4 leading-relaxed">
                  "{section.text}"
                </p>
                {section.author && (
                  <p className="text-berleto-sage text-sm">
                    — {section.author}
                  </p>
                )}
              </div>
            </motion.div>
          </ScrollAnimatedSection>
        );
      
      case 'list':
        return (
          <ScrollAnimatedSection key={index} delay={0.1}>
            <div className="space-y-6 my-8">
              {section.items.map((item, idx) => (
                <motion.div
                  key={idx}
                  className="bg-white border border-berleto-sand/30 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ x: 8 }}
                >
                  <h4 className="text-xl font-medium text-berleto-warmgray mb-2">
                    {item.title}
                  </h4>
                  <p className="text-berleto-sage leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </ScrollAnimatedSection>
        );
      
      case 'highlight':
        return (
          <ScrollAnimatedSection key={index} delay={0.1}>
            <motion.div 
              className="my-12 bg-gradient-to-br from-berleto-rust/10 to-berleto-sage/10 rounded-2xl p-8 border-2 border-berleto-rust/20"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-2xl font-medium text-berleto-rust mb-4">
                {section.title}
              </h4>
              <p className="text-lg text-berleto-sage leading-relaxed">
                {section.text}
              </p>
            </motion.div>
          </ScrollAnimatedSection>
        );
      
      case 'cta':
        return (
          <ScrollAnimatedSection key={index} delay={0.1}>
            <motion.div 
              className="my-16 bg-gradient-to-br from-berleto-warmgray to-berleto-greenblack rounded-3xl p-12 text-center text-white"
              whileHover={{ scale: 1.02 }}
            >
              <h3 className="text-3xl md:text-4xl font-light mb-4">
                {section.title}
              </h3>
              <p className="text-xl text-berleto-sand/90 mb-8 max-w-2xl mx-auto">
                {section.text}
              </p>
              <Button
                onClick={() => navigate(section.buttonLink)}
                className="bg-white text-berleto-warmgray hover:bg-berleto-sand transition-all"
              >
                {section.buttonText}
              </Button>
            </motion.div>
          </ScrollAnimatedSection>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navigation 
        onCartOpen={() => setIsCartOpen(true)} 
        cartItemsCount={cartItemsCount}
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-gradient-to-b from-berleto-offwhite to-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-berleto-sage hover:text-berleto-rust transition-colors mb-8"
            whileHover={{ x: -5 }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Blog</span>
          </motion.button>

          <ScrollAnimatedSection>
            <div className="mb-6">
              <span className="inline-block bg-berleto-rust/10 text-berleto-rust text-sm font-medium px-4 py-2 rounded-full">
                {post.category}
              </span>
            </div>

            <motion.h1 
              className="text-4xl md:text-6xl font-light text-berleto-warmgray mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {post.title}
            </motion.h1>

            {post.subtitle && (
              <motion.p
                className="text-xl md:text-2xl text-berleto-sage mb-8 font-light"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              >
                {post.subtitle}
              </motion.p>
            )}

            <motion.div 
              className="flex flex-wrap items-center gap-6 text-berleto-sage"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span className="font-medium">{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{new Date(post.date).toLocaleDateString('en-US', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{post.readTime} read</span>
              </div>
            </motion.div>
          </ScrollAnimatedSection>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8">
        <div className="container mx-auto px-6 max-w-5xl">
          <ScrollAnimatedSection>
            <motion.div
              className="relative h-96 md:h-[500px] rounded-3xl overflow-hidden shadow-2xl"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </ScrollAnimatedSection>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            {post.content.sections.map((section, index) => 
              renderSection(section, index)
            )}
          </div>

          {post.sources?.length > 0 && (
            <section aria-label="Article sources" className="mt-12 pt-8 border-t border-berleto-sand/30">
              <h2 className="text-2xl font-light text-berleto-warmgray mb-4">
                Sources & Further Reading
              </h2>
              <ul className="space-y-3 text-berleto-sage">
                {post.sources.map((source) => (
                  <li key={source.url}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-4 hover:text-berleto-rust transition-colors"
                    >
                      {source.title}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Tags */}
          <ScrollAnimatedSection>
            <div className="mt-16 pt-8 border-t border-berleto-sand/30">
              <div className="flex items-center gap-3 flex-wrap">
                <Tag className="w-5 h-5 text-berleto-sage" />
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-berleto-offwhite text-berleto-sage px-4 py-2 rounded-full text-sm hover:bg-berleto-sand transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </ScrollAnimatedSection>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-berleto-offwhite">
          <div className="container mx-auto px-6">
            <ScrollAnimatedSection>
              <h2 className="text-3xl md:text-4xl font-light text-berleto-warmgray mb-12 text-center">
                Related Articles
              </h2>
            </ScrollAnimatedSection>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {relatedPosts.map((relatedPost, index) => (
                <ScrollAnimatedSection key={relatedPost.id} delay={index * 0.1}>
                  <motion.article
                    onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                    className="group cursor-pointer bg-white rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-500"
                    whileHover={{ y: -8 }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <motion.img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-2xl font-light text-berleto-warmgray mb-3 group-hover:text-berleto-rust transition-colors">
                        {relatedPost.title}
                      </h3>
                      <p className="text-berleto-sage mb-4 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-berleto-sage/70">
                        <span>{relatedPost.readTime}</span>
                        <motion.div
                          className="text-berleto-rust"
                          whileHover={{ x: 5 }}
                        >
                          <ArrowRight className="w-5 h-5" />
                        </motion.div>
                      </div>
                    </div>
                  </motion.article>
                </ScrollAnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      
      <SideCart 
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}